import { lazy, Suspense } from "react";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";

import { ErrorBoundary } from "@/components/ErrorBoundary";

import { RequireAuth } from "@/components/RequireAuth";

import { RequireAdmin } from "@/components/RequireAdmin";

import { MatterProvider } from "@/contexts/MatterContext";

const Design = lazy(() =>
  import("@/pages/Design").then((module) => ({ default: module.Design })),
);

const Hearing = lazy(() =>
  import("@/pages/Hearing").then((module) => ({ default: module.Hearing })),
);

import { Login } from "@/pages/Login";

import { Home } from "@/pages/Home";

const Activate = lazy(() =>
  import("@/pages/Activate").then((module) => ({ default: module.Activate })),
);

const Settings = lazy(() =>
  import("@/pages/Settings").then((module) => ({ default: module.Settings })),
);

const Admin = lazy(() =>
  import("@/pages/Admin").then((module) => ({ default: module.Admin })),
);

const Prep = lazy(() =>
  import("@/pages/Prep").then((module) => ({ default: module.Prep })),
);

const BattleMap = lazy(() =>
  import("@/pages/BattleMap").then((module) => ({ default: module.BattleMap })),
);

const Witnesses = lazy(() =>
  import("@/pages/Witnesses").then((module) => ({ default: module.Witnesses })),
);

const CrossExamPlanPage = lazy(() =>
  import("@/pages/CrossExamPlanPage").then((module) => ({
    default: module.CrossExamPlanPage,
  })),
);

const Deadlines = lazy(() =>
  import("@/pages/Deadlines").then((module) => ({ default: module.Deadlines })),
);

const MatterEditor = lazy(() =>
  import("@/pages/MatterEditor").then((module) => ({
    default: module.MatterEditor,
  })),
);

const MatterSharing = lazy(() =>
  import("@/pages/MatterSharing").then((module) => ({
    default: module.MatterSharing,
  })),
);

const Matters = lazy(() =>
  import("@/pages/Matters").then((module) => ({ default: module.Matters })),
);

const Sessions = lazy(() =>
  import("@/pages/Sessions").then((module) => ({ default: module.Sessions })),
);

const AdminImport = lazy(() =>
  import("@/pages/AdminImport").then((module) => ({
    default: module.AdminImport,
  })),
);

const AdminLegalSkills = lazy(() =>
  import("@/pages/AdminLegalSkills").then((module) => ({
    default: module.AdminLegalSkills,
  })),
);

const AdminAuthorities = lazy(() =>
  import("@/pages/AdminAuthorities").then((module) => ({
    default: module.AdminAuthorities,
  })),
);

const AdminLearning = lazy(() =>
  import("@/pages/AdminLearning").then((module) => ({
    default: module.AdminLearning,
  })),
);

const AdminMatterSkills = lazy(() =>
  import("@/pages/AdminMatterSkills").then((module) => ({
    default: module.AdminMatterSkills,
  })),
);

const AdminMatterDocuments = lazy(() =>
  import("@/pages/AdminMatterDocuments").then((module) => ({
    default: module.AdminMatterDocuments,
  })),
);

// /sessions browser is built in 5.5.

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Suspense
            fallback={
              <div
                role="status"
                className="grid min-h-screen place-items-center bg-stone-950 text-stone-300"
              >
                Opening your workspace…
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />

              <Route
                path="/matters/:id/sharing"
                element={
                  <RequireAuth>
                    <MatterSharing />
                  </RequireAuth>
                }
              />

              <Route path="/login" element={<Login />} />

              <Route path="/activate/:token" element={<Activate />} />

              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Settings />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <MatterProvider>
                        <Admin />
                      </MatterProvider>
                    </RequireAdmin>
                  </RequireAuth>
                }
              />

              <Route
                path="/hearing"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Hearing />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Matters />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/new"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <MatterEditor />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/:id"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <MatterEditor />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/:id/prep"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Prep />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/:id/witnesses"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Witnesses />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/plans/:planId"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <CrossExamPlanPage />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/:id/battle-map"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <BattleMap />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/matters/:id/deadlines"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Deadlines />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/sessions"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <Sessions />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/import"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminImport />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/legal-skills"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminLegalSkills />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/authorities"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminAuthorities />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/learning"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminLearning />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/matters/:matterId/skills"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminMatterSkills />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/matters/:matterId/documents"
                element={
                  <RequireAuth>
                    <MatterProvider>
                      <AdminMatterDocuments />
                    </MatterProvider>
                  </RequireAuth>
                }
              />

              <Route path="/design" element={<Design />} />

              <Route path="*" element={<Navigate to="/hearing" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
